<template>
  <div class="scan-sheet" role="dialog" aria-modal="true" :aria-label="title" @click.self="$emit('close')">
    <div class="scan-sheet__panel">
      <div class="scan-sheet__top">
        <span class="scan-sheet__title">{{ title }}</span>
        <button ref="closeButton" type="button" class="scan-sheet__round" aria-label="Close scanner" @click="$emit('close')">
          <i class="fa-regular fa-xmark" aria-hidden="true"></i>
        </button>
      </div>

      <div class="scan-sheet__context">
        <template v-if="intent.type === 'sign-out' && intent.sessionName">
          Signing out of
          <b>{{ intent.sessionName }}<template v-if="intent.room"> · {{ intent.room }}</template></b>
        </template>
        <template v-else>Signing in to a session</template>
      </div>

      <div class="scan-sheet__camera">
        <qrcode-stream :camera="camera" :torch="torchOn" @decode="onDecode" @init="onInit">
          <span class="scan-sheet__viewfinder" aria-hidden="true"></span>

          <div v-if="busy" class="scan-sheet__busy">
            <span class="scan-sheet__spinner" aria-hidden="true"></span>
          </div>
        </qrcode-stream>

        <p class="scan-sheet__hint">Fit the QR code inside the frame</p>

        <div class="scan-sheet__tools">
          <button
            v-if="torchSupported"
            type="button"
            class="scan-sheet__round scan-sheet__round--tool"
            :class="{ 'is-on': torchOn }"
            :aria-pressed="torchOn ? 'true' : 'false'"
            :aria-label="torchOn ? 'Turn off flashlight' : 'Turn on flashlight'"
            @click="torchOn = !torchOn"
          >
            <i class="fa-regular fa-flashlight" aria-hidden="true"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { QrcodeStream } from "vue-qrcode-reader";
import { preferPrimaryCameraLens, releasePrimaryCameraLens, SCANNER_CAMERA_RESTART_EVENT } from "../../helpers/camera";

const CAMERA_ERRORS = {
  NotAllowedError: 'Camera access was denied. Please allow camera access and try again.',
  NotFoundError: 'No camera was found on this device.',
  NotSupportedError: 'A secure connection (HTTPS) is required to use the camera.',
  NotReadableError: 'The camera is already in use by another app.',
  OverconstrainedError: 'The installed cameras are not suitable.',
  StreamApiNotSupportedError: 'This browser does not support camera scanning.',
  InsecureContextError: 'Camera access is only allowed over HTTPS.',
};

export default {
  components: {
    QrcodeStream,
  },
  props: {
    // { type: 'sign-in' | 'sign-out', sessionName?, room? } — what the attendee
    // expects the scan to do; the server still decides.
    intent: { type: Object, required: true },
    // A scan is being sent: the camera is paused and further codes ignored.
    busy: { type: Boolean, default: false },
  },
  data() {
    return {
      camera: 'auto',
      pausedInBackground: false,
      lastToken: '',
      torchSupported: false,
      torchOn: false,
      previousOverflow: '',
    }
  },
  computed: {
    title() {
      return this.intent.type === 'sign-out' ? 'Scan to sign out' : 'Scan to sign in';
    },
  },
  watch: {
    busy(busy) {
      if (busy) {
        this.camera = 'off';
      } else if (!this.pausedInBackground) {
        this.camera = 'auto';
      }
    },
  },
  created() {
    // Must run before <qrcode-stream> mounts and requests the camera.
    preferPrimaryCameraLens();
    this.previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  },
  mounted() {
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener(SCANNER_CAMERA_RESTART_EVENT, this.onCameraRestart);
    this.$nextTick(() => this.$refs.closeButton && this.$refs.closeButton.focus({ preventScroll: true }));
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener(SCANNER_CAMERA_RESTART_EVENT, this.onCameraRestart);
    document.body.style.overflow = this.previousOverflow;
    releasePrimaryCameraLens();
  },
  methods: {
    onDecode(token) {
      if (!token || this.busy || token === this.lastToken) {
        return;
      }

      this.lastToken = token;
      this.$emit('decode', token);
    },

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.$emit('close');
      }
    },

    // iOS interrupts capture while the app (or the Median wrapper) is in the
    // background and may end the track for good, leaving a frozen frame on
    // return. Release the camera on hide and request a fresh stream on show.
    onVisibilityChange() {
      if (document.hidden) {
        if (this.camera !== 'off') {
          this.pausedInBackground = true;
          this.camera = 'off';
        }

        return;
      }

      if (this.pausedInBackground && !this.busy) {
        this.pausedInBackground = false;
        this.camera = 'auto';
      }
    },

    // The OS ended the camera and the app is back in the foreground. Flipping
    // between the two rear settings re-opens it (the camera helper sends the
    // same request for both) without passing through 'off'.
    onCameraRestart() {
      if (this.busy || this.camera === 'off') {
        return;
      }

      this.camera = this.camera === 'auto' ? 'rear' : 'auto';
    },

    async onInit(promise) {
      try {
        const { capabilities } = await promise;

        // 'off' resolves with no capabilities; keep what the live camera reported.
        if (this.camera !== 'off') {
          this.torchSupported = !!(capabilities && capabilities.torch);
        }
      } catch (error) {
        this.$emit('error', CAMERA_ERRORS[error.name] || `Camera error (${error.name}).`);
        this.$emit('close');
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.scan-sheet {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  background-color: var(--scan-sheet-bg);
  color: var(--scan-sheet-ink);

  @media (min-width: 901px) {
    align-items: center;
    justify-content: center;
    padding: var(--attendee-gap-lg);
    background-color: var(--scan-sheet-veil);
  }

  &__panel {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
    padding-top: env(safe-area-inset-top);
    background-color: var(--scan-sheet-bg);

    @media (min-width: 901px) {
      flex: 0 1 480px;
      height: min(720px, 90vh);
      padding-top: 0;
      overflow: hidden;
      border-radius: 20px;
      box-shadow: 0 24px 60px var(--scan-sheet-shadow);
    }
  }

  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 16px 0;
  }

  &__title {
    font-size: 17px;
    font-weight: 600;
  }

  &__round {
    display: inline-grid;
    place-items: center;
    width: 40px;
    height: 40px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background-color: var(--scan-sheet-glass);
    color: var(--scan-sheet-ink);
    font-size: 18px;
    cursor: pointer;

    &--tool {
      width: 52px;
      height: 52px;
      font-size: 20px;
    }

    &.is-on {
      background-color: var(--scan-sheet-ink);
      color: var(--scan-sheet-bg);
    }
  }

  &__context {
    margin: 12px 16px 0;
    padding: 10px 12px;
    border-radius: 14px;
    background-color: var(--scan-sheet-glass-soft);
    font-size: 12.5px;
    line-height: 1.4;

    b {
      display: block;
      margin-top: 2px;
      font-size: 14px;
      overflow-wrap: anywhere;
    }
  }

  &__camera {
    position: relative;
    flex: 1;
    min-height: 0;
    margin-top: 12px;

    // The stream wrapper is height:100%, so it needs this sized parent.
    .qrcode-stream-wrapper {
      position: absolute;
      inset: 0;
    }
  }

  // Corner brackets drawn with gradients: no markup that could catch a tap.
  &__viewfinder {
    --corner: 34px;
    --stroke: 4px;

    position: absolute;
    top: 46%;
    left: 50%;
    width: min(64%, 260px);
    aspect-ratio: 1;
    transform: translate(-50%, -50%);
    pointer-events: none;
    border-radius: 18px;
    box-shadow: 0 0 0 999px var(--scan-sheet-dim);
    background:
      linear-gradient(var(--scan-sheet-ink), var(--scan-sheet-ink)) top left / var(--corner) var(--stroke),
      linear-gradient(var(--scan-sheet-ink), var(--scan-sheet-ink)) top left / var(--stroke) var(--corner),
      linear-gradient(var(--scan-sheet-ink), var(--scan-sheet-ink)) top right / var(--corner) var(--stroke),
      linear-gradient(var(--scan-sheet-ink), var(--scan-sheet-ink)) top right / var(--stroke) var(--corner),
      linear-gradient(var(--scan-sheet-ink), var(--scan-sheet-ink)) bottom left / var(--corner) var(--stroke),
      linear-gradient(var(--scan-sheet-ink), var(--scan-sheet-ink)) bottom left / var(--stroke) var(--corner),
      linear-gradient(var(--scan-sheet-ink), var(--scan-sheet-ink)) bottom right / var(--corner) var(--stroke),
      linear-gradient(var(--scan-sheet-ink), var(--scan-sheet-ink)) bottom right / var(--stroke) var(--corner);
    background-repeat: no-repeat;
  }

  &__busy {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    background-color: var(--scan-sheet-shadow);
  }

  &__spinner {
    width: 36px;
    height: 36px;
    border: 3px solid var(--scan-sheet-track);
    border-top-color: var(--scan-sheet-ink);
    border-radius: 50%;
    animation: scan-sheet-spin 0.8s linear infinite;
  }

  &__hint {
    position: absolute;
    right: 16px;
    bottom: calc(96px + env(safe-area-inset-bottom));
    left: 16px;
    margin: 0;
    color: var(--scan-sheet-hint);
    font-size: 14px;
    text-align: center;
    pointer-events: none;
  }

  &__tools {
    position: absolute;
    right: 0;
    bottom: calc(28px + env(safe-area-inset-bottom));
    left: 0;
    display: flex;
    justify-content: center;
    gap: 24px;
  }
}

@keyframes scan-sheet-spin {
  to { transform: rotate(360deg); }
}
</style>
